/**
 * Auth Recovery Utility
 * Detects fatal auth errors and triggers hard reset recovery
 * 
 * HARD RESET / REDIRECT:
 * Fatal cases (e.g. account deleted) or explicit logout may clear auth and send the user
 * to platform-public. platform-auth must NOT redirect there merely because a protected
 * API had no token while the user was never signed in.
 */

import logger from './logger';

const LOOP_DETECTION_WINDOW_MS = 60000; // 60 seconds
// eslint-disable-next-line no-unused-vars
const MAX_FAILURES_BEFORE_RESET = 3;
const FAILURE_STORAGE_KEY = 'auth_recovery_failures';
const RESET_REASON_STORAGE_KEY = 'auth_reset_reason';

/**
 * Check if an error indicates a fatal auth condition requiring hard reset
 * @param {Error} error - MSAL error object
 * @returns {boolean} True if error requires hard reset
 */
export function isFatalAuthError(error) {
  if (!error) return false;

  const errorMessage = error.message || error.errorMessage || '';
  const errorCode = error.errorCode || '';
  const errorDescription = error.errorDescription || '';
  const errorCodes = error.errorCodes || [];
  
  // Fatal errors that require hard reset (user account issues):
  // - AADSTS500341: User account was deleted
  // - user_account_deleted: Account no longer exists
  if (errorCode === 'invalid_grant' || 
      errorMessage.includes('AADSTS500341') ||
      errorDescription.includes('AADSTS500341') ||
      errorCodes.includes(500341) ||
      errorMessage.includes('user_account_deleted') ||
      errorDescription.includes('user_account_deleted') ||
      error.suberror === 'user_account_deleted') {
    return true;
  }

  // All other errors (including AADSTS160021, monitor_window_timeout, no_tokens_found)
  // are NOT fatal - they should be handled by normal re-authentication flow
  return false;
}

/**
 * Check if an error indicates the user doesn't exist in Entra External ID
 * This is different from account deleted - it means the user was never created
 * or was created in a different tenant/flow
 * @param {Error|string} error - Error object or message
 * @returns {boolean} True if user doesn't exist
 */
export function isUserNotFoundError(error) {
  if (!error) return false;
  
  const errorStr = typeof error === 'string' 
    ? error.toLowerCase() 
    : (error.message || error.errorMessage || error.errorDescription || '').toLowerCase();
  
  // Check for "user not found" patterns from backend API
  const hasUserNotFound = errorStr.includes('user not found') || 
                          errorStr.includes('user does not exist') ||
                          errorStr.includes('auto-provisioning failed');
  
  return hasUserNotFound;
}

/**
 * Perform immediate auto-logout and redirect to homepage
 * This is called when authentication errors occur that require user to start fresh
 * @param {string} reason - Reason for logout (for logging)
 */
export function performAutoLogout(reason = 'auth_error') {
  logger.info('AUTH-RECOVERY', 'Performing auto-logout', { reason });

  try {
    // Close WebSocket and clear token provider so it does not reconnect with stale token
    import('./wsClient').then(({ disconnectWs }) => { disconnectWs(); }).catch(() => {});
  } catch (_) {}

  try {
    // Clear all MSAL-related storage
    const keysToRemove = [];
    
    // Clear localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('msal') || 
        key.includes('interaction') || 
        key.includes('login') ||
        key.includes('auth') ||
        key.includes('.cache') ||
        key.includes('active-account')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear sessionStorage
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (
        key.includes('msal') || 
        key.includes('interaction') ||
        key.includes('auth')
      )) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    logger.info('AUTH-RECOVERY', 'Cleared auth state, redirecting to unauth (platform-public) so user can log in');
    redirectToUnauthPage();
  } catch (e) {
    logger.error('AUTH-RECOVERY', 'Error during auto-logout', { error: e.message });
    redirectToUnauthPage();
  }
}

/**
 * Redirect to the unauth (platform-public) page so the user can log in properly.
 * If we are already on platform-public (same origin), do nothing — the user is
 * already on the right page and redirecting would cause an infinite reload loop.
 */
function redirectToUnauthPage() {
  import('./getPublicDomain')
    .then(({ getPublicDomain, isOnPlatformAuth }) => {
      if (!isOnPlatformAuth()) {
        // Already on platform-public — no redirect needed.
        logger.info('AUTH-RECOVERY', 'redirectToUnauthPage: already on platform-public, skipping redirect');
        return;
      }
      const url = getPublicDomain();
      if (url && url !== window.location.origin) {
        window.location.href = url;
      }
    })
    .catch(() => {
      // If we cannot determine the public domain, do nothing rather than looping.
    });
}

/**
 * Track auth failure and determine if hard reset is needed
 * @param {Error} error - MSAL error object
 * @returns {boolean} True if hard reset should be triggered
 */
export function trackFailure(error) {
  if (!error) return false;

  const now = Date.now();
  let failures = [];

  try {
    const stored = sessionStorage.getItem(FAILURE_STORAGE_KEY);
    if (stored) {
      failures = JSON.parse(stored);
    }
  } catch (e) {
    // Ignore parse errors
  }

  // Filter out failures older than the window
  // eslint-disable-next-line no-unused-vars
  failures = failures.filter(f => (now - f.timestamp) < LOOP_DETECTION_WINDOW_MS);

  // Only trigger hard reset for fatal errors (AADSTS500341/user_account_deleted)
  // Generic silent failures should NOT trigger hard reset - they should just prompt for re-auth
  if (isFatalAuthError(error)) {
    // Clear failures and trigger immediate reset
    sessionStorage.removeItem(FAILURE_STORAGE_KEY);
    return true;
  }

  // All other errors (AADSTS160021, monitor_window_timeout, no_tokens_found, etc.)
  // are NOT fatal - return false to allow normal re-authentication flow
  return false;
}

/**
 * Clear failure tracking (call after successful auth)
 */
export function clearFailureTracking() {
  try {
    sessionStorage.removeItem(FAILURE_STORAGE_KEY);
  } catch (e) {
    // Ignore
  }
}

/**
 * Store reset reason for display on logged-out page
 * @param {string} reason - Reason for reset (e.g., 'user_account_deleted', 'loop_detected', 'user_logout')
 */
export function setResetReason(reason) {
  try {
    sessionStorage.setItem(RESET_REASON_STORAGE_KEY, JSON.stringify({
      reason,
      timestamp: Date.now()
    }));
  } catch (e) {
    // Ignore
  }
}

/**
 * Get and clear reset reason
 * @returns {string|null} Reset reason or null
 */
export function getResetReason() {
  try {
    const stored = sessionStorage.getItem(RESET_REASON_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      // Clear after reading (one-time use)
      sessionStorage.removeItem(RESET_REASON_STORAGE_KEY);
      return data.reason;
    }
  } catch (e) {
    // Ignore
  }
  return null;
}

/**
 * Determine if error indicates user account was deleted
 * @param {Error} error - MSAL error object
 * @returns {boolean} True if user account was deleted
 */
export function isUserAccountDeleted(error) {
  if (!error) return false;

  const errorMessage = error.message || error.errorMessage || '';
  const errorDescription = error.errorDescription || '';
  const errorCodes = error.errorCodes || [];

  return error.errorCode === 'invalid_grant' ||
    errorMessage.includes('AADSTS500341') ||
    errorDescription.includes('AADSTS500341') ||
    errorCodes.includes(500341) ||
    errorMessage.includes('user_account_deleted') ||
    errorDescription.includes('user_account_deleted') ||
    error.suberror === 'user_account_deleted';
}

